/**
 * displayNode — single display-layer data model that the exploration graph
 * renders from.
 *
 * A `DisplayNode` is a `DiscoveredNode` enriched with everything a node UI
 * needs to render: a registry-backed `typenameDisplay`, the resolved
 * `productKey`, a resource-type / product logo icon element, and the raw
 * product-logo data URI for `<img>`-style fallbacks.
 *
 * All resolution happens in one pure pass (`toDisplayNode`) so React
 * components never have to inspect ARIs, properties, or bridge metadata.
 */

import type { AtiNodeMetadata } from '../utils/ariUtils';

import type { DiscoveredNode } from './cypherQueryService';
import {
	getProductIconForNode,
	getProductIconImportPathForNode,
	getProductLogoUrl,
} from './productIcons';
import { getProductKey, getProductKeyForNode, getProductDisplayNameByKey } from './productRegistry';

// ============================================================================
// Type
// ============================================================================

export type DisplayNode = DiscoveredNode & {
	/**
	 * Pre-computed, user-facing node typename — runs the registry display
	 * map and the External→Product rebrand so UI can render it directly.
	 */
	typenameDisplay: string;
	/** Resolved product key (`confluence`, `bitbucket`, `slack.slack`, …). */
	productKey: string | null;
	/** Resource-type or product-logo icon as a React element, or null. */
	productIcon: JSX.Element | null;
	/**
	 * Resource-type icon **importPath** (e.g. `@atlaskit/icon/core/page`)
	 * from the product registry. Resolved lazily by the renderer via the
	 * `useProductIcon` hook so each icon module is only fetched on demand.
	 * Null when the registry has no icon importPath for this node.
	 */
	productIconImportPath: string | null;
	/** Raw product-logo data URI for `<img>` fallback, or undefined. */
	productLogoUrl: string | undefined;
	/**
	 * For user-typed nodes: a profile-picture URL surfaced from hydration
	 * (e.g. `properties.picture` on AtlassianAccountUser). Null otherwise.
	 */
	pictureUrl: string | null;
};

// ============================================================================
// Rebrand external → product
// ============================================================================

/**
 * Replaces the leading `"External"` token in a display string with the
 * supplied product name. Returns the original string unchanged when the
 * `External` prefix isn't present so callers can use it unconditionally.
 *
 * Ported from the home explorer's `derivedAtoms.rebrandExternalDisplay`,
 * with one extension: when the product name's **last word** equals the
 * **first word** of the remaining typename, we collapse the duplicate
 * (e.g. `"Google Calendar"` + `"Calendar Event"` → `"Google Calendar
 * Event"` rather than the awkward `"Google Calendar Calendar Event"`).
 */
function rebrandExternalDisplay(display: string, productName: string | null): string {
	if (!productName) return display;
	if (display === 'External') return productName;
	if (display.startsWith('External ')) {
		return collapseBoundaryWord(`${productName} ${display.slice('External '.length)}`);
	}
	return display;
}

/**
 * Collapse a single repeated word at any whitespace boundary,
 * case-insensitive. Only the first occurrence is removed so legitimate
 * downstream repetitions (rare) survive untouched.
 *
 *  - `"Google Calendar Calendar Event"` → `"Google Calendar Event"`
 *  - `"Google Drive Drive Document"`    → `"Google Drive Document"`
 *  - `"Bitbucket Pull Request"`         → unchanged
 */
function collapseBoundaryWord(s: string): string {
	return s.replace(/\b(\w+)\s+\1\b/i, '$1');
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Resolves a human display form of the node's raw TWG typename using the
 * TWG node-name display map, falling back to a PascalCase→spaced form.
 */
function baseTypenameDisplay(
	typename: string,
	typenameDisplayMap: Record<string, string> | undefined,
): string {
	const mapped = typenameDisplayMap?.[typename];
	if (mapped) return mapped;
	return typename.replace(/([a-z])([A-Z])/g, '$1 $2');
}

// ============================================================================
// Enrichment
// ============================================================================

export type ToDisplayNodeContext = {
	/** Optional `typename → display name` map from the TWG node registry. */
	typenameDisplayMap?: Record<string, string>;
	/** Optional ARI metadata map; currently unused but accepted for parity. */
	atiNodeMap?: Record<string, AtiNodeMetadata>;
};

/**
 * Converts a `DiscoveredNode` into a ready-to-render `DisplayNode`.
 *
 * This is a pure function — no Jotai, no React, no network. It pulls the
 * product display name off `properties.productDisplayName` (stamped by the
 * Cypher hydration layer for External graph entities) so the `"External"`
 * prefix gets replaced consistently.
 */
export function toDisplayNode(node: DiscoveredNode, ctx: ToDisplayNodeContext = {}): DisplayNode {
	const productDisplayName: string | null =
		typeof node.properties?.productDisplayName === 'string'
			? node.properties.productDisplayName
			: null;

	const iconInput = { id: node.id, integration: productDisplayName ?? undefined };

	const productKey = getProductKeyForNode(iconInput) || getProductKey(node.id);
	const baseDisplay = baseTypenameDisplay(node.typename, ctx.typenameDisplayMap);
	const typenameDisplay = rebrandExternalDisplay(baseDisplay, productDisplayName);

	const rawPicture = node.properties?.picture;
	const pictureUrl = typeof rawPicture === 'string' && rawPicture ? rawPicture : null;

	return {
		...node,
		typenameDisplay,
		productKey: productKey ?? null,
		productIcon: getProductIconForNode(iconInput),
		productIconImportPath: getProductIconImportPathForNode(iconInput),
		productLogoUrl: getProductLogoUrl(iconInput),
		pictureUrl,
	};
}

/**
 * Convenience: build a pre-hydration placeholder `DisplayNode` directly
 * from an ARI + typename. Callers use this before the actual Cypher
 * hydration lands so the first frame already shows the correct product
 * logo and type label.
 */
export function placeholderDisplayNode(
	ari: string,
	typename: string,
	ctx: ToDisplayNodeContext = {},
): DisplayNode {
	const baseDisplay = baseTypenameDisplay(typename, ctx.typenameDisplayMap);
	const productKey = getProductKey(ari);
	// `productKey` alone cannot tell us whether the node is External*; skip
	// the rebrand here and let hydration replace the placeholder.
	const productDisplayName = productKey ? getProductDisplayNameByKey(productKey) : null;
	const typenameDisplay = rebrandExternalDisplay(baseDisplay, productDisplayName);

	const iconInput = { id: ari };
	return {
		id: ari,
		typename,
		displayName: null,
		url: null,
		properties: {},
		typenameDisplay,
		productKey: productKey ?? null,
		productIcon: getProductIconForNode(iconInput),
		productIconImportPath: getProductIconImportPathForNode(iconInput),
		productLogoUrl: getProductLogoUrl(iconInput),
		pictureUrl: null,
	};
}
